import { UserProfile } from '@shared/types/user';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  OnboardingState,
  OnboardingStep,
  OrganizationInfo,
  OrganizationType
} from '../types';

/**
 * Onboarding store with persistence
 * Manages the state of the onboarding process
 */
export const useOnboardingStore = create<OnboardingState & {
  // Actions
  setCurrentStep: (step: OnboardingStep) => void;
  setOrganizationType: (type: OrganizationType) => void;
  setOrganizationInfo: (info: OrganizationInfo) => void;
  setProfileInfo: (info: Partial<UserProfile>) => void;
  setCompleted: (completed: boolean) => void;
  setReceiveNotifications: (receive: boolean) => void;
  setInvitedUser: (isInvited: boolean, invitationId?: string, organizationId?: string) => void;
  resetOnboarding: () => void;
}>()(
  persist(
    (set) => ({
      currentStep: OnboardingStep.ORGANIZATION_TYPE,
      organizationType: null,
      organizationInfo: null,
      profileInfo: null,
      completed: false,
      receiveNotifications: true,
      isInvitedUser: false,
      invitationId: undefined,
      organizationId: undefined,

      // Set current step
      setCurrentStep: (step: OnboardingStep) => set({ currentStep: step }),

      // Set organization type
      setOrganizationType: (type: OrganizationType) => set({
        organizationType: type,
        currentStep: OnboardingStep.ORGANIZATION_INFO
      }),

      // Set organization information
      setOrganizationInfo: (info: OrganizationInfo) => set({
        organizationInfo: info,
        currentStep: OnboardingStep.PROFILE_INFO
      }),

      // Set profile information
      setProfileInfo: (info: Partial<UserProfile>) => set({
        profileInfo: info,
        currentStep: OnboardingStep.COMPLETE
      }),

      // Set onboarding completion status
      setCompleted: (completed: boolean) => set({ completed }),
      
      // Set receive notifications preference
      setReceiveNotifications: (receiveNotifications: boolean) => set({ receiveNotifications }),

      // Set invited user status and related information
      setInvitedUser: (isInvitedUser: boolean, invitationId?: string, organizationId?: string) => set({
        isInvitedUser,
        invitationId,
        organizationId
      }),

      // Reset onboarding state
      resetOnboarding: () => set({
        currentStep: OnboardingStep.ORGANIZATION_TYPE,
        organizationType: null,
        organizationInfo: null,
        profileInfo: null,
        completed: false,
        receiveNotifications: true,
        isInvitedUser: false,
        invitationId: undefined,
        organizationId: undefined
      })
    }),
    {
      name: 'onboarding-storage',
    }
  )
);
